/** Units for human-readable file sizes (binary, base-1024). */
const UNITS = ['B', 'KB', 'MB', 'GB'] as const

/**
 * Formats a byte count as a human-readable string using binary divisors (1024).
 *
 * @example
 * formatFileSize(0)       // "0 B"
 * formatFileSize(1536)    // "1.5 KB"
 * formatFileSize(1048576) // "1 MB"
 * formatFileSize(4404019) // "4.2 MB"
 *
 * @param bytes - The number of bytes to format.
 * @returns A human-readable string such as `"1.5 KB"` or `"4.2 MB"`.
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) {
    return '0 B'
  }

  const unitIndex = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), UNITS.length - 1)

  if (unitIndex === 0) {
    return `${bytes} B`
  }

  const value = bytes / Math.pow(1024, unitIndex)
  const formatted = parseFloat(value.toFixed(1)).toString()
  return `${formatted} ${UNITS[unitIndex]}`
}
